import React, { useEffect, useState, useMemo } from 'react';
import { sediApi, configApi, form4Api } from '../services/api';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  Title, Tooltip, Legend,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Bar, Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, zoomPlugin);

interface SediTransaction {
  id: number;
  sediid: string | null;
  name: string | null;
  ticker: string | null;
  ownerName: string | null;
  title: string | null;
  securityTitle: string | null;
  date: string | null;
  reportDate: string | null;
  transactionType: string | null;
  code: string | null;
  shares: number | null;
  price: number | null;
  amount: number | null;
  totalShares: number | null;
  ownership: string | null;
  exchange: string | null;
}

interface SediPreview {
  sediid: string; name: string; ticker: string; ownerName: string; title: string;
  date: string; type: string; code: string; shares: number; price: number;
  amount: number; totalShares: number; qualified: boolean;
}

interface DailyData {
  date: string;
  buyCount: number;
  sellCount: number;
  buyAmount: number;
  sellAmount: number;
}

interface TopTicker {
  ticker: string;
  count: number;
  amount: number;
}

interface DashboardData {
  daily: DailyData[];
  topBuys30: TopTicker[];
  topSells30: TopTicker[];
  topBuys90: TopTicker[];
  topSells90: TopTicker[];
}

interface DateInfo {
  date: string;
  total: number;
  buys: number;
  sells: number;
}

type TabKey = 'dashboard' | 'latestBuy' | 'latestSell' | 'weeklyBuy' | 'weeklySell' | 'search' | 'upload' | 'fixTickers' | 'maintenance';

const tabLabels: { key: TabKey; label: string }[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'latestBuy', label: 'Latest Buys' },
  { key: 'latestSell', label: 'Latest Sells' },
  { key: 'weeklyBuy', label: 'Weekly Buys' },
  { key: 'weeklySell', label: 'Weekly Sells' },
  { key: 'search', label: 'Search' },
  { key: 'upload', label: 'Upload SEDI Report' },
  { key: 'fixTickers', label: 'Fix Missing Tickers' },
  { key: 'maintenance', label: 'Maintenance' },
];

const tickerSuffix = (exchange: string | null) =>
  exchange === 'TSE' ? '.TO' : exchange === 'CVE' ? '.V' : '';

const xTicker = (ticker: string | null, exchange: string | null) =>
  `${ticker || ''}${tickerSuffix(exchange)}`;

const yahooSuffix = (exchange: string | null) =>
  exchange === 'TSE' ? '.TO' : exchange === 'CVE' ? '.V' : exchange === 'CSE' ? '.CN' : '';

const yahooUrl = (ticker: string | null, exchange: string | null) =>
  `https://finance.yahoo.com/quote/${(ticker || '')}${yahooSuffix(exchange)}/`;

const generateTweet = (t: SediTransaction): string => {
  const name = (t.name || '')
    .replace(/\s*\/\w+\/\s*/g, ' ')
    .replace(/,?\s*Inc\.?/g, '')
    .replace(/,?\s*Corp\.?/g, '')
    .replace(/,?\s*Ltd\.?/g, '')
    .replace(/,?\s*L\.?L\.?C\.?/g, '')
    .replace(/,?\s*L\.?P\.?/g, '')
    .replace(/,?\s*plc\.?/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  const owner = t.ownerName || '';
  const title = (t.title || '').replace(/ [oO]f .*$/, '').replace(/^\d{1,2} - /, '');
  const action = t.transactionType === 'Buy' ? 'bought' : 'sold';
  const emoji = t.transactionType === 'Buy' ? '🟢' : '🔴';
  const shares = (t.shares || 0).toLocaleString();
  const price = (t.price || 0).toFixed(2);
  const amt = t.amount || 0;
  const amountStr = amt >= 1_000_000
    ? `$${(amt / 1_000_000).toFixed(2)}M`
    : `$${amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const total = (t.totalShares || 0).toLocaleString();
  const ticker = xTicker(t.ticker, t.exchange);
  const exchangeLabel = t.exchange === 'TSE' ? 'TSX' : t.exchange === 'CVE' ? 'TSX-V' : (t.exchange || '');
  // Derive a short company hashtag from the cleaned name (first meaningful word)
  const companyTag = name.split(/[\s,]+/).filter(w => w.length > 1)[0] || '';

  // Date line: show both dates if they differ, collapse if same
  const fmtDate = (d: string) => {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const dateParts: string[] = [];
  if (t.date && t.reportDate && t.date !== t.reportDate) {
    dateParts.push(`Txn ${fmtDate(t.date)}`);
    dateParts.push(`Filed ${fmtDate(t.reportDate)}`);
  } else {
    dateParts.push(fmtDate(t.reportDate || t.date || ''));
  }
  dateParts.push(exchangeLabel);
  dateParts.push('SEDI');
  const dateLine = dateParts.join(' \u00b7 ');

  const lines = [
    `${emoji} ${owner}, ${title} of ${name} just ${action} ${amountStr} of $${ticker}`,
    ``,
    `${shares} shares at $${price} \u00b7 Now holds ${total} shares`,
    ``,
    dateLine,
    `#InsiderTrading${companyTag ? ` #${companyTag}` : ''} #Stocks #Trading`,
  ];
  return lines.join('\n');
};

const copyToClipboard = (text: string) => {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  } else {
    fallbackCopy(text);
  }
};

const fallbackCopy = (text: string) => {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
};

const fmtAmt = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : `$${Math.round(n).toLocaleString()}`;

/* ---- Post to X ---- */
const PostButton: React.FC<{ getText: () => string; chartTicker?: string; chartTxnDate?: string; chartTxnType?: string }> = ({ getText, chartTicker, chartTxnDate, chartTxnType }) => {
  const [status, setStatus] = useState<'idle' | 'copied' | 'posting' | 'posted' | 'error'>('idle');
  const handleCopy = () => { copyToClipboard(getText()); setStatus('copied'); setTimeout(() => setStatus('idle'), 2000); };
  const handlePost = () => {
    const text = getText();
    if (text.length > 25000) { alert(`Tweet too long (${text.length}/25000 chars).`); return; }
    if (!window.confirm(`Post to X?\n\n${text}`)) return;
    setStatus('posting');
    form4Api.postToX(text, false, 3600, chartTicker, chartTxnDate, chartTxnType)
      .then(() => { setStatus('posted'); setTimeout(() => setStatus('idle'), 5000); })
      .catch((err: any) => { setStatus('error'); alert(`Post failed: ${err?.response?.data?.detail || err.message}`); setTimeout(() => setStatus('idle'), 3000); });
  };
  const postColor = status === 'posted' ? 'green' : status === 'error' ? 'red' : '#e04530';
  const postLabel = status === 'posting' ? 'Posting...' : status === 'posted' ? 'Posted!' : status === 'error' ? 'Failed' : 'Post to X';
  return (
    <span style={{ marginLeft: '8px', fontSize: '0.75em' }}>
      <span style={{ cursor: status === 'posting' ? 'wait' : 'pointer', color: postColor, fontWeight: 'bold', marginRight: 6 }}
        onClick={status === 'posting' ? undefined : handlePost}>{postLabel}</span>
      <span style={{ cursor: 'pointer', color: status === 'copied' ? 'green' : '#888' }}
        onClick={handleCopy}>{status === 'copied' ? 'Copied!' : 'Copy'}</span>
    </span>
  );
};

/* ---- Tweet generators ---- */
const tweetTransactionList = (items: SediTransaction[], tabLabel: string) => {
  if (!items.length) return '';
  const type = items[0]?.transactionType === 'Buy' ? 'buy' : 'sell';
  const emoji = type === 'buy' ? '\uD83D\uDFE2' : '\uD83D\uDD34';
  const lines = items.slice(0, 20).map((t) => {
    const amt = t.amount || 0;
    const amtStr = amt >= 1_000_000 ? `$${(amt / 1_000_000).toFixed(1)}M` : `$${Math.round(amt).toLocaleString()}`;
    return `$${xTicker(t.ticker, t.exchange)} — ${t.ownerName?.split(';')[0] || ''} ${type === 'buy' ? 'bought' : 'sold'} ${amtStr}`;
  });
  const tickers = Array.from(new Set(items.slice(0, 10).map(t => `$${xTicker(t.ticker, t.exchange)}`))).join(' ');
  return [
    `${emoji} SEDI ${tabLabel}`,
    ``,
    ...lines,
    ``,
    tickers,
    ``,
    `#SEDI #InsiderTrading #TSX #Insider${type === 'buy' ? 'Buying' : 'Selling'}`,
  ].join('\n');
};

const tweetBuySellCount = (daily: DailyData[], type: 'buy' | 'sell') => {
  const emoji = type === 'buy' ? '\uD83D\uDFE2' : '\uD83D\uDD34';
  const recent = daily.slice(-30);
  const lines = recent.map(d => `${d.date}: ${type === 'buy' ? d.buyCount : d.sellCount}`);
  return [`${emoji} SEDI Daily ${type === 'buy' ? 'Buy' : 'Sell'} Transactions (Last 30 Report Dates)`, ``, ...lines, ``, `#SEDI #InsiderTrading #TSX`].join('\n');
};

const tweetBuySellAmount = (daily: DailyData[], type: 'buy' | 'sell') => {
  const emoji = type === 'buy' ? '\uD83D\uDFE2' : '\uD83D\uDD34';
  const recent = daily.slice(-30);
  const lines = recent.map(d => `${d.date}: ${fmtAmt(type === 'buy' ? d.buyAmount : d.sellAmount)}`);
  return [`${emoji} SEDI Daily ${type === 'buy' ? 'Buy' : 'Sell'} Amount (Last 30 Report Dates)`, ``, ...lines, ``, `#SEDI #InsiderTrading #TSX`].join('\n');
};

const tweetRatio = (daily: DailyData[]) => {
  const recent = daily.slice(-30);
  const lines = recent.map(d => {
    const cr = d.sellCount > 0 ? (d.buyCount / d.sellCount).toFixed(2) : 'N/A';
    const ar = d.sellAmount > 0 ? (d.buyAmount / d.sellAmount).toFixed(2) : 'N/A';
    return `${d.date}: Count ${cr}, Amount ${ar}`;
  });
  return [`\uD83D\uDCC8 SEDI Buy/Sell Ratio (Last 30 Report Dates)`, ``, ...lines, ``, `#SEDI #InsiderTrading #TSX`].join('\n');
};

const tweetTopTable = (title: string, items: TopTicker[], type: 'buy' | 'sell') => {
  const emoji = type === 'buy' ? '\uD83D\uDFE2' : '\uD83D\uDD34';
  const lines = items.map((t, i) => `${i + 1}. $${t.ticker} — ${fmtAmt(t.amount)} (${t.count} ${t.count === 1 ? 'txn' : 'txns'})`);
  return [`${emoji} ${title}`, ``, ...lines, ``, `#SEDI #InsiderTrading #TSX`].join('\n');
};

/* ---- Dashboard Tab ---- */
const chartOpts = (title: string, totalLabels: number): any => {
  const defaultWindow = 60;
  const minIdx = Math.max(0, totalLabels - defaultWindow);
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' as const, labels: { font: { size: 11 } } },
      title: { display: true, text: title, font: { size: 13 } },
      zoom: {
        pan: { enabled: true, mode: 'x' as const },
        zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' as const },
      },
    },
    scales: {
      x: { min: minIdx, ticks: { font: { size: 9 }, maxRotation: 45 } },
      y: { beginAtZero: true },
    },
  };
};

const SediTopTable: React.FC<{ title: string; items: TopTicker[]; color: string; onTickerClick?: (ticker: string) => void }> = ({ title, items, color, onTickerClick }) => (
  <div>
    <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: '0.3em', color }}>{title}</div>
    <table style={styles.table}>
      <thead><tr>
        <th style={styles.th}>#</th>
        <th style={styles.th}>Ticker</th>
        <th style={styles.th}>Transactions</th>
        <th style={styles.th}>Total Amount</th>
      </tr></thead>
      <tbody>
        {items.map((t, i) => (
          <tr key={t.ticker} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#e6f2ff' }}>
            <td style={{ ...styles.td, textAlign: 'center' }}>{i + 1}</td>
            <td style={styles.td}>
              {onTickerClick ? (
                <span style={{ ...styles.actionLink, fontWeight: 'bold' }} onClick={() => onTickerClick(t.ticker)}>{t.ticker}</span>
              ) : <strong>{t.ticker}</strong>}
            </td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{t.count}</td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{fmtAmt(t.amount)}</td>
          </tr>
        ))}
        {items.length === 0 && <tr><td colSpan={4} style={{ ...styles.td, color: '#999' }}>No data</td></tr>}
      </tbody>
    </table>
  </div>
);

interface DailyPick {
  id: number;
  pick_date: string;
  rank_num: number;
  ticker: string;
  yf_ticker: string | null;
  exchange: string | null;
  total_score: number;
  db_score: number | null;
  scores: Record<string, number> | null;
  price_data: Record<string, number> | null;
  buyback_count: number;
}

interface PickDateInfo { date: string; count: number; }

const SediTopPicksSection: React.FC<{ onSearchTicker: (ticker: string) => void }> = ({ onSearchTicker }) => {
  const [picks, setPicks] = useState<DailyPick[]>([]);
  const [dates, setDates] = useState<PickDateInfo[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [prevTickers, setPrevTickers] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [genDate, setGenDate] = useState(() => new Date().toISOString().slice(0, 10));

  const loadPrevTickers = (currentDate: string, datesList: PickDateInfo[]) => {
    const idx = datesList.findIndex(d => d.date === currentDate);
    if (idx >= 0 && idx < datesList.length - 1) {
      const prevDate = datesList[idx + 1].date;
      sediApi.dailyPicks(prevDate)
        .then(res => setPrevTickers(new Set(res.data.map((p: DailyPick) => p.ticker))))
        .catch(() => setPrevTickers(new Set()));
    } else {
      setPrevTickers(new Set());
    }
  };

  useEffect(() => {
    Promise.all([sediApi.dailyPicks(), sediApi.dailyPicksDates()])
      .then(([picksRes, datesRes]) => {
        setPicks(picksRes.data);
        setDates(datesRes.data);
        if (picksRes.data.length > 0) {
          setSelectedDate(picksRes.data[0].pick_date);
          loadPrevTickers(picksRes.data[0].pick_date, datesRes.data);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDateChange = (d: string) => {
    setSelectedDate(d);
    setLoading(true);
    sediApi.dailyPicks(d)
      .then((res) => { setPicks(res.data); loadPrevTickers(d, dates); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleGenerate = () => {
    if (!window.confirm(`Generate picks for ${genDate}? This may take a few minutes.`)) return;
    setGenerating(true);
    setMessage('');
    sediApi.dailyPicksGenerate(genDate)
      .then((res) => {
        const d = res.data;
        setMessage(`Generated ${d.count} picks for ${d.date}`);
        // Reload
        return Promise.all([sediApi.dailyPicks(), sediApi.dailyPicksDates()]);
      })
      .then(([picksRes, datesRes]) => {
        setPicks(picksRes.data);
        setDates(datesRes.data);
        if (picksRes.data.length > 0) setSelectedDate(picksRes.data[0].pick_date);
      })
      .catch((err: any) => setMessage(`Error: ${err?.response?.data?.detail || err.message}`))
      .finally(() => setGenerating(false));
  };

  const scoreLabel = (key: string) => {
    const map: Record<string, string> = {
      continuity: 'Cont', position: 'Pos', buy_price_flat: 'BPF', price_36m: 'P36M',
      trend: 'Trend', buy_count: 'BuyCt', buyback_bonus: 'BB', bottom: 'Bttm',
      abs_price: 'AbsP', insider_bonus: 'InsBon', sell_penalty: 'SellP',
    };
    return map[key] || key;
  };

  const scoreKeys = ['continuity', 'position', 'buy_price_flat', 'price_36m', 'trend', 'buy_count', 'buyback_bonus', 'bottom', 'abs_price', 'insider_bonus', 'sell_penalty'];

  return (
    <div style={{ marginBottom: '1.5em' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1em', marginBottom: '0.5em' }}>
        <span style={{ fontWeight: 'bold', fontSize: '0.95em', color: '#0069cc' }}>Top Picks</span>
        {dates.length > 0 && (
          <select value={selectedDate} onChange={(e) => handleDateChange(e.target.value)}
            style={{ padding: '2px 4px', fontSize: '0.85em' }}>
            {dates.map((d) => (
              <option key={d.date} value={d.date}>{d.date} ({d.count} picks)</option>
            ))}
          </select>
        )}
        <input type="date" value={genDate} onChange={(e) => setGenDate(e.target.value)}
          style={{ padding: '2px 4px', fontSize: '0.85em' }} />
        <button onClick={handleGenerate} disabled={generating}
          style={{ ...styles.button, fontSize: '0.8em', padding: '2px 10px' }}>
          {generating ? 'Generating...' : 'Generate'}
        </button>
        {message && <span style={{ fontSize: '0.8em', color: message.startsWith('Error') ? 'red' : 'green' }}>{message}</span>}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '1em', fontSize: '0.85em' }}>Loading picks...</div>
      ) : picks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1em', fontSize: '0.85em', color: '#999' }}>
          No picks available. Click "Generate Now" to create today's picks.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Ticker</th>
                <th style={styles.th}>Exchange</th>
                <th style={styles.th}>Score</th>
                <th style={styles.th}>Price</th>
                {scoreKeys.map((k) => (
                  <th key={k} style={{ ...styles.th, fontSize: '0.7em' }}>{scoreLabel(k)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {picks.map((p, i) => {
                const s = p.scores || {};
                const pd = p.price_data || {};
                const isNew = prevTickers.size > 0 && !prevTickers.has(p.ticker);
                return (
                  <tr key={p.id} style={{ backgroundColor: isNew ? '#ffffcc' : (i % 2 === 0 ? '#fff' : '#e6f2ff') }}>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{p.rank_num}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.actionLink, fontWeight: 'bold' }} onClick={() => onSearchTicker(p.ticker)}>{p.ticker}</span>
                      {isNew && <span style={{ marginLeft: 4, fontSize: '0.65em', color: '#e65100', fontWeight: 'bold' }}>NEW</span>}
                      {p.yf_ticker && p.yf_ticker !== p.ticker && (
                        <a href={`https://finance.yahoo.com/quote/${p.yf_ticker}/`} target="_blank" rel="noreferrer"
                          style={{ marginLeft: 4, fontSize: '0.75em', color: '#666' }}>
                          {p.yf_ticker}
                        </a>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center', fontSize: '0.75em' }}>{p.exchange || ''}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 'bold' }}>{p.total_score.toFixed(2)}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{pd.current_price != null ? `$${pd.current_price.toFixed(2)}` : ''}</td>
                    {scoreKeys.map((k) => (
                      <td key={k} style={{ ...styles.td, textAlign: 'right', fontSize: '0.75em' }}>
                        {s[k] != null ? (typeof s[k] === 'number' ? s[k].toFixed(1) : s[k]) : ''}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SediDashboardTab: React.FC<{ onSearchTicker: (ticker: string) => void }> = ({ onSearchTicker }) => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sediApi.dashboard()
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>;
  if (!data) return <div style={{ textAlign: 'center', padding: '2em', color: '#999' }}>No data</div>;

  const labels = data.daily.map(d => d.date);

  const buyCountData = { labels, datasets: [{ label: 'Buy Transactions', data: data.daily.map(d => d.buyCount), backgroundColor: 'rgba(76, 175, 80, 0.7)' }] };
  const sellCountData = { labels, datasets: [{ label: 'Sell Transactions', data: data.daily.map(d => d.sellCount), backgroundColor: 'rgba(244, 67, 54, 0.7)' }] };
  const buyAmountData = { labels, datasets: [{ label: 'Buy Amount ($)', data: data.daily.map(d => d.buyAmount), backgroundColor: 'rgba(76, 175, 80, 0.7)' }] };
  const sellAmountData = { labels, datasets: [{ label: 'Sell Amount ($)', data: data.daily.map(d => d.sellAmount), backgroundColor: 'rgba(244, 67, 54, 0.7)' }] };

  const defaultWindow = 60;
  const ratioMinIdx = Math.max(0, labels.length - defaultWindow);
  const ratioData = {
    labels,
    datasets: [
      { label: 'Buy/Sell Ratio (Count)', data: data.daily.map(d => d.sellCount > 0 ? +(d.buyCount / d.sellCount).toFixed(2) : 0), borderColor: '#2196f3', backgroundColor: 'rgba(33, 150, 243, 0.1)', tension: 0.3, fill: false, yAxisID: 'yCount' },
      { label: 'Buy/Sell Ratio (Amount)', data: data.daily.map(d => d.sellAmount > 0 ? +(d.buyAmount / d.sellAmount).toFixed(2) : 0), borderColor: '#ff9800', backgroundColor: 'rgba(255, 152, 0, 0.1)', tension: 0.3, fill: false, yAxisID: 'yAmount' },
    ],
  };
  const ratioOpts: any = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' as const, labels: { font: { size: 11 } } },
      title: { display: true, text: 'Buy/Sell Ratio Over Time', font: { size: 13 } },
      zoom: { pan: { enabled: true, mode: 'x' as const }, zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' as const } },
    },
    scales: {
      x: { min: ratioMinIdx, ticks: { font: { size: 9 }, maxRotation: 45 } },
      yCount: { type: 'linear' as const, position: 'left' as const, beginAtZero: true, title: { display: true, text: 'Ratio (Count)', font: { size: 11 }, color: '#2196f3' }, ticks: { color: '#2196f3' } },
      yAmount: { type: 'linear' as const, position: 'right' as const, beginAtZero: true, title: { display: true, text: 'Ratio (Amount)', font: { size: 11 }, color: '#ff9800' }, ticks: { color: '#ff9800' }, grid: { drawOnChartArea: false } },
    },
  };

  return (
    <div style={{ marginTop: '0.5em' }}>
      <SediTopPicksSection onSearchTicker={onSearchTicker} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em', marginBottom: '1em' }}>
        <div style={{ height: 220, padding: '8px', border: '1px solid #ddd', borderRadius: 4, position: 'relative' }}>
          <Bar data={buyCountData} options={chartOpts('Daily Buy Transactions (Count)', labels.length)} />
          <PostButton getText={() => tweetBuySellCount(data.daily, 'buy')} />
        </div>
        <div style={{ height: 220, padding: '8px', border: '1px solid #ddd', borderRadius: 4, position: 'relative' }}>
          <Bar data={sellCountData} options={chartOpts('Daily Sell Transactions (Count)', labels.length)} />
          <PostButton getText={() => tweetBuySellCount(data.daily, 'sell')} />
        </div>
        <div style={{ height: 220, padding: '8px', border: '1px solid #ddd', borderRadius: 4, position: 'relative' }}>
          <Bar data={buyAmountData} options={chartOpts('Daily Buy Amount ($)', labels.length)} />
          <PostButton getText={() => tweetBuySellAmount(data.daily, 'buy')} />
        </div>
        <div style={{ height: 220, padding: '8px', border: '1px solid #ddd', borderRadius: 4, position: 'relative' }}>
          <Bar data={sellAmountData} options={chartOpts('Daily Sell Amount ($)', labels.length)} />
          <PostButton getText={() => tweetBuySellAmount(data.daily, 'sell')} />
        </div>
      </div>
      <div style={{ height: 250, padding: '8px', border: '1px solid #ddd', borderRadius: 4, marginBottom: '1em', position: 'relative' }}>
        <Line data={ratioData} options={ratioOpts} />
        <PostButton getText={() => tweetRatio(data.daily)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em', marginBottom: '1em' }}>
        <div>
          <SediTopTable title="Top 10 Insider Buys (Last 30 Days)" items={data.topBuys30} color="green" onTickerClick={onSearchTicker} />
          <PostButton getText={() => tweetTopTable('Top 10 SEDI Insider Buys (Last 30 Days)', data.topBuys30, 'buy')} />
        </div>
        <div>
          <SediTopTable title="Top 10 Insider Sells (Last 30 Days)" items={data.topSells30} color="red" onTickerClick={onSearchTicker} />
          <PostButton getText={() => tweetTopTable('Top 10 SEDI Insider Sells (Last 30 Days)', data.topSells30, 'sell')} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em' }}>
        <div>
          <SediTopTable title="Top 10 Insider Buys (Last 90 Days)" items={data.topBuys90} color="green" onTickerClick={onSearchTicker} />
          <PostButton getText={() => tweetTopTable('Top 10 SEDI Insider Buys (Last 90 Days)', data.topBuys90, 'buy')} />
        </div>
        <div>
          <SediTopTable title="Top 10 Insider Sells (Last 90 Days)" items={data.topSells90} color="red" onTickerClick={onSearchTicker} />
          <PostButton getText={() => tweetTopTable('Top 10 SEDI Insider Sells (Last 90 Days)', data.topSells90, 'sell')} />
        </div>
      </div>
    </div>
  );
};

/* ---- Dates Summary (used inside Search tab) ---- */
const SediDatesSummary: React.FC = () => {
  const [dates, setDates] = useState<DateInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    sediApi.dates()
      .then((res) => setDates(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (date: string) => {
    if (!window.confirm(`Delete all SEDI transactions for ${date}?`)) return;
    try {
      const res = await sediApi.deleteByDate(date);
      setMessage(`Deleted ${res.data.deleted} transactions for ${date}.`);
      setDates(prev => prev.filter(d => d.date !== date));
    } catch (err: any) {
      setMessage(`Error: ${err.message}`);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '1em', fontSize: '0.85em' }}>Loading dates...</div>;
  if (dates.length === 0) return null;

  return (
    <div style={{ marginBottom: '1em' }}>
      <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: '0.3em' }}>Report Dates (Last 60)</div>
      {message && (
        <div style={{ fontSize: '0.85em', marginBottom: '0.5em', padding: '6px 12px', backgroundColor: message.startsWith('Error') ? '#f8d7da' : '#d4edda', borderRadius: '4px', fontWeight: 'bold' }}>
          {message}
        </div>
      )}
      <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd' }}>
        <table style={{ ...styles.table, marginTop: 0 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}><tr>
            <th style={styles.th}>Report Date</th>
            <th style={styles.th}>Total</th>
            <th style={styles.th}>Buys</th>
            <th style={styles.th}>Sells</th>
            <th style={styles.th}>Actions</th>
          </tr></thead>
          <tbody>
            {dates.map((d, idx) => (
              <tr key={d.date} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                <td style={styles.td}>{d.date}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{d.total}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: 'green' }}>{d.buys}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: 'red' }}>{d.sells}</td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <span style={{ ...styles.actionLink, color: '#c00' }} onClick={() => handleDelete(d.date)}>Delete</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---- Maintenance Tab (Outlier Detection) ---- */
interface SediOutlier extends SediTransaction {
  outlier_reason?: string;
}

type OutlierSortKey = 'reportDate' | 'ticker' | 'owner' | 'date' | 'type' | 'shares' | 'price' | 'amount' | 'reason';

const SediMaintenanceTab: React.FC<{ onSearchTicker?: (ticker: string) => void }> = ({ onSearchTicker }) => {
  const [outliers, setOutliers] = useState<SediOutlier[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sortKey, setSortKey] = useState<OutlierSortKey>('amount');
  const [sortAsc, setSortAsc] = useState(false);
  const [delStart, setDelStart] = useState('');
  const [delEnd, setDelEnd] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loadOutliers = () => {
    setLoading(true);
    setMessage('');
    sediApi.outliers()
      .then((res) => setOutliers(res.data))
      .catch((err: any) => setMessage(`Error: ${err.message}`))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadOutliers(); }, []);

  const handleSort = (key: OutlierSortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(key === 'ticker' || key === 'owner' || key === 'reportDate' || key === 'date'); }
  };

  const sorted = useMemo(() => {
    const arr = [...outliers];
    const dir = sortAsc ? 1 : -1;
    arr.sort((a, b) => {
      let av: any, bv: any;
      switch (sortKey) {
        case 'reportDate': av = a.reportDate || ''; bv = b.reportDate || ''; break;
        case 'ticker': av = a.ticker || ''; bv = b.ticker || ''; break;
        case 'owner': av = a.ownerName || ''; bv = b.ownerName || ''; break;
        case 'date': av = a.date || ''; bv = b.date || ''; break;
        case 'type': av = a.transactionType || ''; bv = b.transactionType || ''; break;
        case 'shares': av = a.shares || 0; bv = b.shares || 0; break;
        case 'price': av = a.price || 0; bv = b.price || 0; break;
        case 'amount': av = a.amount || 0; bv = b.amount || 0; break;
        case 'reason': av = a.outlier_reason || ''; bv = b.outlier_reason || ''; break;
      }
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
    return arr;
  }, [outliers, sortKey, sortAsc]);

  const handleQuickRemove = async (t: SediOutlier) => {
    try {
      await sediApi.delete(t.id);
      setOutliers(prev => prev.filter(o => o.id !== t.id));
    } catch (err: any) {
      setMessage(`Error removing: ${err.message}`);
    }
  };

  const handleRemove = async (t: SediOutlier) => {
    if (!window.confirm(`Remove outlier transaction for ${t.ticker}?\nAmount: $${(t.amount || 0).toLocaleString()}\nReason: ${t.outlier_reason}`)) return;
    try {
      await sediApi.delete(t.id);
      setOutliers(prev => prev.filter(o => o.id !== t.id));
      setMessage(`Removed transaction for ${t.ticker}.`);
    } catch (err: any) {
      setMessage(`Error removing: ${err.message}`);
    }
  };

  const handleRemoveAll = async () => {
    if (!window.confirm(`Remove ALL ${outliers.length} outlier transactions? This cannot be undone.`)) return;
    let removed = 0;
    for (const t of outliers) {
      try { await sediApi.delete(t.id); removed++; } catch { /* skip */ }
    }
    setMessage(`Removed ${removed} of ${outliers.length} outlier transactions.`);
    loadOutliers();
  };

  const sortArrow = (key: OutlierSortKey) => sortKey === key ? (sortAsc ? ' \u25B2' : ' \u25BC') : '';

  return (
    <div style={{ marginTop: '0.5em' }}>
      <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', fontSize: '0.85em', marginBottom: '1em' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '1em' }}>Outlier Detection</div>
        <p style={{ margin: '0 0 8px 0', color: '#555' }}>
          Finds SEDI transactions with unusual amounts that may be caused by data entry errors.
          Criteria: statistical outliers (Z-score &gt; 3), amount mismatches (shares x price vs amount), and extremely large amounts (&gt; $100M).
        </p>
        <div style={{ display: 'flex', gap: '0.5em' }}>
          <input type="button" value="Refresh" onClick={loadOutliers} disabled={loading}
            style={{ ...styles.button, backgroundColor: '#b3d9ff', fontWeight: 'bold' }} />
          {outliers.length > 0 && (
            <input type="button" value={`Remove All (${outliers.length})`} onClick={handleRemoveAll}
              style={{ ...styles.button, backgroundColor: '#ffcccc', fontWeight: 'bold' }} />
          )}
        </div>
      </div>

      {/* Delete by date range */}
      <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', fontSize: '0.85em', marginBottom: '1em' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '1em' }}>Delete Data by Date Range</div>
        <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
          <label>From:</label>
          <input type="date" value={delStart} onChange={(e) => setDelStart(e.target.value)}
            style={{ ...styles.input, width: '11em' }} />
          <label>To:</label>
          <input type="date" value={delEnd} onChange={(e) => setDelEnd(e.target.value)}
            style={{ ...styles.input, width: '11em' }} />
          <input type="button"
            value={deleting ? 'Deleting...' : 'Delete Range'}
            disabled={deleting || !delStart || !delEnd}
            onClick={async () => {
              if (!window.confirm(`Delete ALL SEDI data from ${delStart} to ${delEnd}? This cannot be undone.`)) return;
              setDeleting(true);
              try {
                const res = await sediApi.deleteByDateRange(delStart, delEnd);
                setMessage(`Deleted ${res.data.deleted} transactions from ${delStart} to ${delEnd}.`);
              } catch (err: any) {
                setMessage(`Error: ${err.response?.data?.detail || err.message}`);
              } finally {
                setDeleting(false);
              }
            }}
            style={{ ...styles.button, backgroundColor: '#ffcccc', fontWeight: 'bold' }} />
        </div>
      </div>

      {message && (
        <div style={{
          fontSize: '0.85em', marginBottom: '0.5em', padding: '6px 12px',
          backgroundColor: message.startsWith('Error') ? '#f8d7da' : '#d4edda',
          borderRadius: '4px', fontWeight: 'bold',
        }}>
          {message}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2em' }}>Scanning for outliers...</div>
      ) : outliers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2em', color: '#999' }}>No outliers detected. Data looks clean!</div>
      ) : (
        <>
          <div style={{ fontSize: '0.85em', marginBottom: '0.5em', color: '#555' }}>
            Found <strong>{outliers.length}</strong> potential outlier transactions. Click column headers to sort.
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('reportDate')}>Report Date{sortArrow('reportDate')}</th>
                <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('ticker')}>Ticker{sortArrow('ticker')}</th>
                <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('owner')}>Owner{sortArrow('owner')}</th>
                <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('date')}>Date{sortArrow('date')}</th>
                <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('type')}>Type{sortArrow('type')}</th>
                <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('shares')}>Shares{sortArrow('shares')}</th>
                <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('price')}>Price{sortArrow('price')}</th>
                <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('amount')}>Amount{sortArrow('amount')}</th>
                <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('reason')}>Outlier Reason{sortArrow('reason')}</th>
                <th style={styles.th}>Remove</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t, idx) => {
                const isBuy = t.transactionType === 'Buy';
                return (
                  <tr key={t.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                    <td style={styles.td}>{t.reportDate}</td>
                    <td style={styles.td}>
                      {onSearchTicker ? (
                        <span style={{ ...styles.actionLink, fontWeight: 'bold' }} onClick={() => onSearchTicker(t.ticker || '')}>{t.ticker}</span>
                      ) : <strong>{t.ticker}</strong>}
                    </td>
                    <td style={styles.td}>{t.ownerName}</td>
                    <td style={styles.td}>{t.date}</td>
                    <td style={{ ...styles.td, textAlign: 'center', color: isBuy ? 'green' : 'red', fontWeight: 'bold' }}>
                      {t.transactionType}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{t.shares?.toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{t.price?.toFixed(2)}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 'bold', color: '#c00' }}>
                      {t.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ ...styles.td, fontSize: '0.75em', color: '#666' }}>{t.outlier_reason}</td>
                    <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <span style={{ ...styles.actionLink, color: '#c00' }} onClick={() => handleQuickRemove(t)}>X</span>
                      <span style={{ margin: '0 4px', color: '#ccc' }}>|</span>
                      <span style={{ ...styles.actionLink, color: '#888' }} onClick={() => handleRemove(t)}>?</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

type SediSortKey = 'name' | 'ticker' | 'ownerName' | 'title' | 'date' | 'reportDate' | 'shares' | 'price' | 'amount' | 'totalShares';

const SediPage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('latestBuy');
  const [items, setItems] = useState<SediTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTicker, setSearchTicker] = useState('');
  const [searchAmount, setSearchAmount] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [tweetId, setTweetId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{ ticker: string; shares: number; price: number; amount: number; totalShares: number } | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [sortKey, setSortKey] = useState<SediSortKey>('reportDate');
  const [sortAsc, setSortAsc] = useState(false);

  const handleSort = (key: SediSortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(key === 'name' || key === 'ticker' || key === 'ownerName' || key === 'title'); }
  };

  const sortedItems = useMemo(() => {
    const getValue = (row: SediTransaction, key: SediSortKey): number | string => {
      switch (key) {
        case 'name': return row.name || '';
        case 'ticker': return row.ticker || '';
        case 'ownerName': return row.ownerName || '';
        case 'title': return row.title || '';
        case 'date': return row.date ? new Date(row.date).getTime() : 0;
        case 'reportDate': return row.reportDate ? new Date(row.reportDate).getTime() : 0;
        case 'shares': return row.shares || 0;
        case 'price': return row.price || 0;
        case 'amount': return row.amount || 0;
        case 'totalShares': return row.totalShares || 0;
      }
    };

    const cmp = (x: number | string, y: number | string, direction: number) => {
      if (x < y) return -direction;
      if (x > y) return direction;
      return 0;
    };

    const arr = [...items];
    const dir = sortAsc ? 1 : -1;
    arr.sort((a, b) => {
      const primary = cmp(getValue(a, sortKey), getValue(b, sortKey), dir);
      if (primary !== 0) return primary;

      // Secondary for string columns only: amount desc so biggest transactions surface first.
      // Date / reportDate / numeric columns sort purely by their own value.
      if (sortKey === 'name' || sortKey === 'ticker' || sortKey === 'ownerName' || sortKey === 'title') {
        return cmp(a.amount || 0, b.amount || 0, -1);
      }
      return 0;
    });
    return arr;
  }, [items, sortKey, sortAsc]);

  const sortArrow = (key: SediSortKey) => sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '';
  const thSort = (key: SediSortKey, label: string) => (
    <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(key)}>
      {label}{sortArrow(key)}
    </th>
  );

  const loadTab = (t: TabKey) => {
    if (t === 'search' || t === 'upload' || t === 'fixTickers' || t === 'dashboard' || t === 'maintenance') { setLoading(false); return; }
    setLoading(true);
    const fn = { latestBuy: sediApi.latestBuy, latestSell: sediApi.latestSell, weeklyBuy: sediApi.weeklyBuy, weeklySell: sediApi.weeklySell }[t as 'latestBuy' | 'latestSell' | 'weeklyBuy' | 'weeklySell'];
    if (fn) fn().then((res: any) => setItems(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadTab(tab); }, [tab]);

  const handleSearch = () => {
    setLoading(true);
    setSelected(new Set());
    const params: any = {};
    if (searchTicker) params.ticker = searchTicker;
    else if (searchAmount) params.min_amount = parseFloat(searchAmount);
    sediApi.search(params).then((res) => setItems(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  const handleSearchTicker = (ticker: string) => {
    setSearchTicker(ticker);
    setItems([]);
    setSelected(new Set());
    setTab('search');
    setLoading(true);
    sediApi.search({ ticker }).then((res) => setItems(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  const toggleSelect = (id: number) => {
    setSelected(prev => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  };
  const toggleSelectAll = () => {
    if (selected.size === sortedItems.length) setSelected(new Set());
    else setSelected(new Set(sortedItems.map(t => t.id)));
  };
  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (!window.confirm(`Remove ${ids.length} selected transaction(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await sediApi.bulkDelete(ids);
      setItems(prev => prev.filter(i => !selected.has(i.id)));
      setSelected(new Set());
    } catch (err: any) {
      alert(`Error: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBulkDeleting(false);
    }
  };
  const handleRemoveAll = async () => {
    const ids = sortedItems.map(t => t.id);
    if (!window.confirm(`Remove ALL ${ids.length} records in results? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await sediApi.bulkDelete(ids);
      setItems([]);
      setSelected(new Set());
    } catch (err: any) {
      alert(`Error: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleEdit = (t: SediTransaction) => {
    setEditId(t.id);
    setEditForm({ ticker: t.ticker || '', shares: t.shares || 0, price: t.price || 0, amount: t.amount || 0, totalShares: t.totalShares || 0 });
  };
  const handleEditSave = async () => {
    if (editId == null || !editForm) return;
    try {
      await sediApi.update(editId, editForm);
      setItems(prev => prev.map(i => i.id === editId ? { ...i, ...editForm } : i));
      setEditId(null);
      setEditForm(null);
    } catch (err: any) {
      alert(`Error: ${err?.response?.data?.detail || err.message}`);
    }
  };
  const handleEditCancel = () => { setEditId(null); setEditForm(null); };

  return (
    <div style={styles.container}>
      <h4 style={styles.heading}>SEDI Insider Transactions (Canada)</h4>
      <div style={styles.tabs}>
        {tabLabels.map((t) => (
          <span
            key={t.key}
            onClick={() => { setTab(t.key); if (t.key !== 'search' && t.key !== 'dashboard' && t.key !== 'maintenance') setItems([]); }}
            style={{ ...styles.tab, ...(tab === t.key ? styles.activeTab : {}) }}
          >
            {t.label}
          </span>
        ))}
      </div>

      {tab === 'dashboard' && <SediDashboardTab onSearchTicker={handleSearchTicker} />}
      {tab === 'maintenance' && <SediMaintenanceTab onSearchTicker={handleSearchTicker} />}

      {tab === 'search' && (
        <>
          <SediDatesSummary />
          <div style={styles.searchBar}>
            <label>Ticker: </label>
            <input value={searchTicker} onChange={(e) => setSearchTicker(e.target.value.toUpperCase())} style={styles.input} />
            <label style={{ marginLeft: '1em' }}>Min Amount: </label>
            <input value={searchAmount} onChange={(e) => setSearchAmount(e.target.value)} style={{ ...styles.input, width: '8em' }} />
            <input type="button" value="Search" onClick={handleSearch} style={{ ...styles.button, marginLeft: '1em' }} />
          </div>
          {items.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5em', marginTop: '0.5em', alignItems: 'center' }}>
              <span style={{ fontSize: '0.85em', color: '#555' }}>{items.length} results</span>
              {selected.size > 0 && (
                <button onClick={handleBulkDelete} disabled={bulkDeleting}
                  style={{ ...styles.button, backgroundColor: '#ffcccc', fontWeight: 'bold', fontSize: '0.8em' }}>
                  {bulkDeleting ? 'Removing...' : `Remove Selected (${selected.size})`}
                </button>
              )}
              <button onClick={handleRemoveAll} disabled={bulkDeleting}
                style={{ ...styles.button, backgroundColor: '#ffcccc', fontWeight: 'bold', fontSize: '0.8em' }}>
                {bulkDeleting ? 'Removing...' : `Remove All (${items.length})`}
              </button>
            </div>
          )}
        </>
      )}

      {!loading && items.length > 0 && ['latestBuy', 'latestSell', 'weeklyBuy', 'weeklySell'].includes(tab) && (
        <div style={{ marginBottom: '0.5em' }}>
          <PostButton getText={() => tweetTransactionList(items, tabLabels.find(t => t.key === tab)?.label || tab)} />
        </div>
      )}

      {(tab === 'upload' || tab === 'fixTickers' || tab === 'dashboard' || tab === 'maintenance') ? null : loading ? (
        <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              {tab === 'search' && (
                <th style={{ ...styles.th, width: '30px' }}>
                  <input type="checkbox" checked={sortedItems.length > 0 && selected.size === sortedItems.length}
                    onChange={toggleSelectAll} />
                </th>
              )}
              {thSort('name', 'Name')}
              {thSort('ticker', 'Ticker')}
              {thSort('ownerName', 'Owner')}
              {thSort('title', 'Title')}
              <th style={styles.th}>Security</th>
              {thSort('date', 'Date')}
              {thSort('reportDate', 'Report Date')}
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Code</th>
              {thSort('shares', 'Shares')}
              {thSort('price', 'Price')}
              {thSort('amount', 'Amount')}
              {thSort('totalShares', 'Total Shares')}
              <th style={styles.th}>Ownership</th>
              <th style={styles.th}>Details</th>
              <th style={styles.th}>Tweet</th>
              {tab === 'search' && <th style={styles.th}>Edit</th>}
              <th style={styles.th}>Remove</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((t, idx) => {
              const isCSE = t.exchange === 'CSE';
              const rowBg = idx % 2 === 0 ? '#fff' : '#e6f2ff';
              const isBuy = t.transactionType === 'Buy';
              const typeColor = isCSE ? '#aaa' : (isBuy ? 'green' : 'red');
              return (
                <React.Fragment key={t.id}>
                  <tr style={{ backgroundColor: rowBg, ...(isCSE ? { color: '#aaa' } : {}) }}>
                    {tab === 'search' && (
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} />
                      </td>
                    )}
                    <td style={styles.td}>{t.name}</td>
                    {tab === 'search' && editId === t.id && editForm ? (
                      <td style={styles.td}><input value={editForm.ticker} onChange={(e) => setEditForm({ ...editForm, ticker: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '5em' }} /></td>
                    ) : (
                      <td style={styles.td}>
                        {tab === 'search' ? (
                          <a href={yahooUrl(t.ticker, t.exchange)} target="_blank" rel="noreferrer"
                            style={{ fontWeight: 'bold', color: '#0069cc', textDecoration: 'none' }}>{t.ticker}</a>
                        ) : (
                          <span style={{ ...styles.actionLink, fontWeight: 'bold' }}
                            onClick={() => handleSearchTicker(t.ticker || '')}>{t.ticker}</span>
                        )}
                      </td>
                    )}
                    <td style={styles.td}>{t.ownerName}</td>
                    <td style={styles.td}>{t.title}</td>
                    <td style={styles.td}>{t.securityTitle}</td>
                    <td style={styles.td}>{t.date}</td>
                    <td style={styles.td}>{t.reportDate}</td>
                    <td style={{ ...styles.td, textAlign: 'center', color: typeColor, fontWeight: 'bold' }}>{t.transactionType}</td>
                    <td style={{ ...styles.td, textAlign: 'center', color: typeColor }}>{t.code}</td>
                    {tab === 'search' && editId === t.id && editForm ? (
                      <>
                        <td style={styles.td}><input type="number" value={editForm.shares} onChange={(e) => { const s = parseInt(e.target.value) || 0; setEditForm({ ...editForm, shares: s, amount: s * editForm.price }); }} style={{ ...styles.input, width: '6em' }} /></td>
                        <td style={styles.td}><input type="number" step="0.01" value={editForm.price} onChange={(e) => { const p = parseFloat(e.target.value) || 0; setEditForm({ ...editForm, price: p, amount: editForm.shares * p }); }} style={{ ...styles.input, width: '6em' }} /></td>
                        <td style={styles.td}><input type="number" step="0.01" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '8em' }} /></td>
                        <td style={styles.td}><input type="number" value={editForm.totalShares} onChange={(e) => setEditForm({ ...editForm, totalShares: parseInt(e.target.value) || 0 })} style={{ ...styles.input, width: '7em' }} /></td>
                      </>
                    ) : (
                      <>
                        <td style={{ ...styles.td, textAlign: 'right', color: typeColor }}>{t.shares?.toLocaleString()}</td>
                        <td style={{ ...styles.td, textAlign: 'right', color: typeColor }}>{t.price?.toFixed(2)}</td>
                        <td style={{ ...styles.td, textAlign: 'right', color: typeColor }}>{t.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>{t.totalShares?.toLocaleString()}</td>
                      </>
                    )}
                    <td style={styles.td}>{t.ownership}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <span style={styles.actionLink} onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}>
                        {expandedId === t.id ? 'Hide' : 'Show'}
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <span style={styles.actionLink} onClick={() => setTweetId(tweetId === t.id ? null : t.id)}>Tweet</span>
                    </td>
                    {tab === 'search' && (
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        {editId === t.id ? (
                          <>
                            <span style={{ ...styles.actionLink, color: 'green' }} onClick={handleEditSave}>Save</span>
                            {' '}
                            <span style={styles.actionLink} onClick={handleEditCancel}>Cancel</span>
                          </>
                        ) : (
                          <span style={styles.actionLink} onClick={() => handleEdit(t)}>Edit</span>
                        )}
                      </td>
                    )}
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <span style={{ ...styles.actionLink, color: '#c00' }} onClick={async () => {
                        if (!window.confirm(`Remove SEDI transaction ${t.sediid} for ${t.ticker}?`)) return;
                        await sediApi.delete(t.id);
                        setItems(prev => prev.filter(i => i.id !== t.id));
                      }}>Remove</span>
                    </td>
                  </tr>
                  {expandedId === t.id && (
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <td colSpan={18} style={{ ...styles.td, padding: '8px 12px', fontSize: '0.85em' }}>
                        <strong>SEDI ID:</strong> {t.sediid} &nbsp;|&nbsp;
                        <strong>Name:</strong> {t.name} &nbsp;|&nbsp;
                        <strong>Ticker:</strong> {t.ticker} &nbsp;|&nbsp;
                        <strong>Owner:</strong> {t.ownerName} &nbsp;|&nbsp;
                        <strong>Title:</strong> {t.title} &nbsp;|&nbsp;
                        <strong>Security:</strong> {t.securityTitle} &nbsp;|&nbsp;
                        <strong>Date:</strong> {t.date} &nbsp;|&nbsp;
                        <strong>Report Date:</strong> {t.reportDate} &nbsp;|&nbsp;
                        <strong>Type:</strong> {t.transactionType} &nbsp;|&nbsp;
                        <strong>Code:</strong> {t.code} &nbsp;|&nbsp;
                        <strong>Shares:</strong> {t.shares?.toLocaleString()} &nbsp;|&nbsp;
                        <strong>Price:</strong> {t.price?.toFixed(2)} &nbsp;|&nbsp;
                        <strong>Amount:</strong> {t.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} &nbsp;|&nbsp;
                        <strong>Total Shares:</strong> {t.totalShares?.toLocaleString()} &nbsp;|&nbsp;
                        <strong>Ownership:</strong> {t.ownership}
                      </td>
                    </tr>
                  )}
                  {tweetId === t.id && (
                    <tr style={{ backgroundColor: '#fff9e6' }}>
                      <td colSpan={18} style={{ ...styles.td, padding: '8px 12px', fontSize: '0.85em', whiteSpace: 'pre-wrap' }}>
                        <strong>Tweet:</strong>{'\n'}{generateTweet(t)}
                        <PostButton getText={() => generateTweet(t)}
                          chartTicker={`${t.ticker || ''}${yahooSuffix(t.exchange)}`}
                          chartTxnDate={t.date || undefined}
                          chartTxnType={t.transactionType === 'Buy' ? 'buy' : 'sell'} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
      {!loading && items.length === 0 && tab !== 'search' && tab !== 'upload' && tab !== 'dashboard' && tab !== 'fixTickers' && tab !== 'maintenance' && <p style={{ textAlign: 'center', color: '#999' }}>No transactions found</p>}

      {tab === 'upload' && <SediUploadTab />}
      {tab === 'fixTickers' && <FixMissingTickersTab />}
    </div>
  );
};

/* ---- SEDI Upload Tab ---- */
const SEDI_URL = 'https://www.sedi.ca/sedi/SVTReportsAccessController?menukey=null&locale=en_CA';

const SediUploadTab: React.FC = () => {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const [reportDate, setReportDate] = useState(today);
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [inserting, setInserting] = useState(false);
  const [preview, setPreview] = useState<SediPreview[]>([]);
  const [summary, setSummary] = useState({ parsed: 0, qualified: 0 });
  const [parsed, setParsed] = useState(false);
  const [result, setResult] = useState('');
  const [copied, setCopied] = useState('');

  const copyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 1500);
  };

  const dateObj = new Date(reportDate + 'T12:00:00');
  const sediYear = dateObj.getFullYear().toString();
  const sediMonth = dateObj.toLocaleString('en-US', { month: 'long' });
  const sediDay = dateObj.getDate().toString();

  const handleUpload = async () => {
    if (!file) return;
    setParsing(true);
    setResult('');
    setPreview([]);
    setParsed(false);
    try {
      const r = await configApi.uploadSediFile(file, reportDate);
      setSummary({ parsed: r.data.parsed, qualified: r.data.qualified });
      setPreview(r.data.transactions);
      setParsed(true);
    } catch (e: any) {
      setResult(e.response?.data?.detail || 'Failed to parse file.');
    }
    setParsing(false);
  };

  const handleConfirm = async () => {
    if (!file) return;
    if (!window.confirm(`Insert ${summary.qualified} qualified transaction(s) for ${reportDate}?\nExisting transactions for this date will be replaced.`)) return;
    setInserting(true);
    setResult('');
    try {
      await configApi.confirmSediInsert(file, reportDate);
      setResult(`Done! Inserted ${summary.qualified} qualified SEDI transaction(s) for ${reportDate}.`);
      setPreview([]);
      setFile(null);
    } catch (e: any) {
      setResult(e.response?.data?.detail || 'Failed to insert transactions.');
    }
    setInserting(false);
  };

  return (
    <div style={{ marginTop: '0.5em' }}>
      {/* Step 1 */}
      <div style={{ marginBottom: '1em', padding: '10px 14px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', fontSize: '0.85em' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '1em' }}>Step 1: Download SEDI Report</div>
        <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 'bold' }}>Report Date:</label>
          <input type="date" value={reportDate} onChange={e => setReportDate(e.target.value)}
            style={{ ...styles.input, width: '12em' }} />
          <a href={SEDI_URL} target="_blank" rel="noreferrer"
            style={{ ...styles.button, textDecoration: 'none', display: 'inline-block', backgroundColor: '#b3d9ff', fontWeight: 'bold' }}>
            Open SEDI Website
          </a>
        </div>
        <div style={{ marginBottom: '6px' }}>Copy these values into the SEDI form:</div>
        <table style={{ borderCollapse: 'collapse', fontSize: '0.95em' }}>
          <tbody>
            <tr>
              <td style={{ padding: '2px 8px', fontWeight: 'bold' }}>Report Type:</td>
              <td style={{ padding: '2px 8px' }}>Insider transaction detail</td>
              <td></td>
            </tr>
            <tr>
              <td style={{ padding: '2px 8px', fontWeight: 'bold' }}>Year (From/To):</td>
              <td style={{ padding: '2px 8px', fontFamily: 'monospace', fontSize: '1.05em' }}>{sediYear}</td>
              <td style={{ padding: '2px 8px' }}>
                <span onClick={() => copyText(sediYear, 'year')} style={{ color: '#0069cc', cursor: 'pointer', fontSize: '0.85em' }}>{copied === 'year' ? 'Copied!' : 'Copy'}</span>
              </td>
            </tr>
            <tr>
              <td style={{ padding: '2px 8px', fontWeight: 'bold' }}>Month (From/To):</td>
              <td style={{ padding: '2px 8px', fontFamily: 'monospace', fontSize: '1.05em' }}>{sediMonth}</td>
              <td style={{ padding: '2px 8px' }}>
                <span onClick={() => copyText(sediMonth, 'month')} style={{ color: '#0069cc', cursor: 'pointer', fontSize: '0.85em' }}>{copied === 'month' ? 'Copied!' : 'Copy'}</span>
              </td>
            </tr>
            <tr>
              <td style={{ padding: '2px 8px', fontWeight: 'bold' }}>Day (From/To):</td>
              <td style={{ padding: '2px 8px', fontFamily: 'monospace', fontSize: '1.05em' }}>{sediDay}</td>
              <td style={{ padding: '2px 8px' }}>
                <span onClick={() => copyText(sediDay, 'day')} style={{ color: '#0069cc', cursor: 'pointer', fontSize: '0.85em' }}>{copied === 'day' ? 'Copied!' : 'Copy'}</span>
              </td>
            </tr>
            <tr>
              <td style={{ padding: '2px 8px', fontWeight: 'bold' }}>Security Type:</td>
              <td style={{ padding: '2px 8px' }}>Check "Common Shares"</td>
              <td></td>
            </tr>
          </tbody>
        </table>
        <div style={{ marginTop: '6px', color: '#666', fontSize: '0.9em' }}>
          After clicking Search and the results load, click the <strong>PDF</strong> icon to download the report (recommended), or save the page as HTML.
        </div>
      </div>

      {/* Step 2 */}
      <div style={{ marginBottom: '1em', padding: '10px 14px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', fontSize: '0.85em' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '1em' }}>Step 2: Upload & Parse</div>
        <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" accept=".html,.htm,.pdf" onChange={e => { setFile(e.target.files?.[0] || null); setPreview([]); setResult(''); setParsed(false); }}
            style={{ fontSize: '0.95em' }} />
          <input type="button" value={parsing ? 'Parsing...' : 'Upload & Parse'}
            onClick={handleUpload} disabled={!file || parsing} style={styles.button} />
        </div>
      </div>

      {/* Messages */}
      {result && (
        <div style={{
          fontSize: '0.85em', marginBottom: '0.5em', padding: '6px 12px',
          backgroundColor: result.startsWith('Done') ? '#d4edda' : '#f8d7da',
          borderRadius: '4px', fontWeight: 'bold',
        }}>
          {result}
        </div>
      )}
      {parsed && preview.length === 0 && !result && (
        <div style={{ fontSize: '0.85em', padding: '6px 12px', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '0.5em' }}>
          No transactions found in the uploaded file. Make sure the SEDI search returned results before saving the page.
        </div>
      )}

      {/* Step 3: Preview */}
      {preview.length > 0 && (
        <div style={{ marginBottom: '1em' }}>
          <div style={{ fontSize: '0.85em', marginBottom: '0.5em' }}>
            Parsed <strong>{summary.parsed}</strong> transaction(s), <strong>{summary.qualified}</strong> meet threshold.
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Ticker</th>
                <th style={styles.th}>Issuer</th>
                <th style={styles.th}>Insider</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Shares</th>
                <th style={styles.th}>Price</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Qualified</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((t, idx) => (
                <tr key={idx} style={{
                  backgroundColor: t.qualified
                    ? (t.type === 'Buy' ? '#d4edda' : '#f8d7da')
                    : idx % 2 === 0 ? '#fff' : '#f5f5f5',
                  opacity: t.qualified ? 1 : 0.6,
                }}>
                  <td style={{ ...styles.td, fontWeight: 'bold' }}>{t.ticker}</td>
                  <td style={styles.td}>{t.name}</td>
                  <td style={styles.td}>{t.ownerName}</td>
                  <td style={{ ...styles.td, fontWeight: 'bold', color: t.type === 'Buy' ? 'green' : 'red' }}>{t.type}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{t.shares.toLocaleString()}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>${t.price.toFixed(2)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 'bold' }}>${t.amount.toLocaleString()}</td>
                  <td style={styles.td}>{t.date}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    {t.qualified ? 'Yes' : <span style={{ color: '#999' }}>No</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: '0.5em' }}>
            <input type="button"
              value={inserting ? 'Inserting...' : `Confirm & Insert ${summary.qualified} Transaction(s)`}
              onClick={handleConfirm}
              disabled={inserting || summary.qualified === 0}
              style={{ ...styles.button, backgroundColor: '#b3d9ff', fontWeight: 'bold' }} />
          </div>
        </div>
      )}
    </div>
  );
};

/* ---- Fix Missing Tickers Tab ---- */
interface MissingTicker {
  name: string;
  count: number;
}

interface TickerMatch {
  exchange: string;
  ticker: string;
  name: string;
}

const FixMissingTickersTab: React.FC = () => {
  const [missing, setMissing] = useState<MissingTicker[]>([]);
  const [loading, setLoading] = useState(true);
  const [lookupName, setLookupName] = useState<string | null>(null);
  const [matches, setMatches] = useState<TickerMatch[]>([]);
  const [searching, setSearching] = useState(false);
  const [manualTicker, setManualTicker] = useState('');
  const [manualExchange, setManualExchange] = useState('TSE');
  const [fixing, setFixing] = useState(false);
  const [fixingAll, setFixingAll] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number; name: string; status: string; ticker?: string; source?: string } | null>(null);
  const [fixAllResult, setFixAllResult] = useState<{ fixed: number; skipped: number; total: number } | null>(null);

  const loadMissing = () => {
    setLoading(true);
    sediApi.missingTickers()
      .then((res) => setMissing(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadMissing(); }, []);

  const handleLookup = (name: string) => {
    setLookupName(name);
    setMatches([]);
    setSearching(true);
    setManualTicker('');
    sediApi.lookupTicker(name)
      .then((res) => {
        setMatches(res.data);
        // Auto-fill manual entry from best match
        if (res.data.length > 0) {
          const priority = ['TSE', 'CVE', 'CSE'];
          const best = [...res.data].sort((a: TickerMatch, b: TickerMatch) => {
            const exDiff = priority.indexOf(a.exchange) - priority.indexOf(b.exchange);
            if (exDiff !== 0) return exDiff;
            return a.ticker.length - b.ticker.length;
          })[0];
          setManualExchange(best.exchange);
          setManualTicker(best.ticker);
        }
      })
      .catch(() => {})
      .finally(() => setSearching(false));
  };

  const handleFix = async (companyName: string, exchange: string, ticker: string) => {
    if (!window.confirm(`Map "${companyName}" → ${exchange}:${ticker}?\nThis will update all NONE-ticker transactions for this company and add to stock_symbols.`)) return;
    setFixing(true);
    try {
      const res = await sediApi.fixTicker({ name: companyName, exchange, ticker });
      alert(`Updated ${res.data.updated} transaction(s) to ${ticker}`);
      setLookupName(null);
      setMatches([]);
      loadMissing();
    } catch {
      alert('Failed to fix ticker');
    }
    setFixing(false);
  };

  const handleFixAll = async () => {
    if (!window.confirm('Auto-fix all company names where a ticker match can be found?')) return;
    setFixingAll(true);
    setProgress(null);
    setFixAllResult(null);

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/sedi/fix-all-tickers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error('No reader');

      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const msg = JSON.parse(line.slice(6));
          if (msg.type === 'progress') {
            setProgress({ current: msg.current, total: msg.total, name: msg.name, status: msg.status, ticker: msg.ticker, source: msg.source });
          } else if (msg.type === 'done') {
            setFixAllResult({ fixed: msg.fixed, skipped: msg.skipped, total: msg.total });
          }
        }
      }
      loadMissing();
    } catch {
      alert('Failed to auto-fix tickers');
    }
    setFixingAll(false);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>;
  if (missing.length === 0 && !fixAllResult) return <div style={{ textAlign: 'center', padding: '2em', color: '#090' }}>No missing tickers found.</div>;

  return (
    <div style={{ marginTop: '0.5em' }}>
      <div style={{ fontSize: '0.85em', marginBottom: '0.5em', color: '#666', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{missing.length} company name(s) with unresolved tickers. Click "Lookup" to search for a match in stock_symbols.</span>
        <input type="button" value={fixingAll ? 'Fixing...' : 'Fix All Auto-Resolvable'}
          onClick={handleFixAll} disabled={fixingAll}
          style={{ ...fixStyles.button, backgroundColor: '#b3d9ff', fontWeight: 'bold' }} />
      </div>
      {(fixingAll || fixAllResult) && progress && (
        <div style={{ fontSize: '0.85em', marginBottom: '0.5em', padding: '8px 12px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>
              {fixAllResult
                ? `Done: ${fixAllResult.fixed} fixed, ${fixAllResult.skipped} skipped out of ${fixAllResult.total}`
                : `Processing ${progress.current} / ${progress.total}: ${progress.name}`}
            </span>
            <span>
              {progress.status === 'fixed' && progress.ticker
                ? <span style={{ color: '#090' }}>{progress.ticker} <span style={{ color: '#999', fontSize: '0.85em' }}>({progress.source === 'yahoo' ? 'Yahoo' : 'DB'})</span></span>
                : progress.status === 'no_match'
                ? <span style={{ color: '#999' }}>no match</span>
                : null}
            </span>
          </div>
          <div style={{ width: '100%', backgroundColor: '#ddd', borderRadius: '3px', height: '14px' }}>
            <div style={{
              width: `${Math.round((progress.current / progress.total) * 100)}%`,
              backgroundColor: fixAllResult ? '#090' : '#0069cc',
              height: '100%', borderRadius: '3px',
              transition: 'width 0.2s',
            }} />
          </div>
        </div>
      )}
      <table style={fixStyles.table}>
        <thead>
          <tr>
            <th style={fixStyles.th}>Company Name</th>
            <th style={fixStyles.th}>Transactions</th>
            <th style={fixStyles.th}>Action</th>
          </tr>
        </thead>
        <tbody>
          {missing.map((m, idx) => (
            <React.Fragment key={m.name}>
              <tr style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                <td style={fixStyles.td}>{m.name}</td>
                <td style={{ ...fixStyles.td, textAlign: 'center' }}>{m.count}</td>
                <td style={{ ...fixStyles.td, textAlign: 'center' }}>
                  <span style={fixStyles.actionLink} onClick={() => handleLookup(m.name)}>
                    {lookupName === m.name ? 'Searching...' : 'Lookup'}
                  </span>
                </td>
              </tr>
              {lookupName === m.name && (
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <td colSpan={3} style={{ ...fixStyles.td, padding: '8px 12px' }}>
                    {searching ? (
                      <span style={{ color: '#666', fontSize: '0.85em' }}>Searching stock_symbols...</span>
                    ) : matches.length > 0 ? (
                      <div>
                        <div style={{ fontSize: '0.85em', fontWeight: 'bold', marginBottom: '4px' }}>Matches found:</div>
                        <table style={{ borderCollapse: 'collapse', fontSize: '0.85em', width: '100%' }}>
                          <thead>
                            <tr>
                              <th style={{ ...fixStyles.th, padding: '2px 6px' }}>Exchange</th>
                              <th style={{ ...fixStyles.th, padding: '2px 6px' }}>Ticker</th>
                              <th style={{ ...fixStyles.th, padding: '2px 6px' }}>Name</th>
                              <th style={{ ...fixStyles.th, padding: '2px 6px' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {matches.map((mt, i) => (
                              <tr key={i}>
                                <td style={{ ...fixStyles.td, padding: '2px 6px' }}>{mt.exchange}</td>
                                <td style={{ ...fixStyles.td, padding: '2px 6px', fontWeight: 'bold' }}>{mt.ticker}</td>
                                <td style={{ ...fixStyles.td, padding: '2px 6px' }}>{mt.name}</td>
                                <td style={{ ...fixStyles.td, padding: '2px 6px', textAlign: 'center' }}>
                                  <span style={{ ...fixStyles.actionLink, color: '#090' }}
                                    onClick={() => !fixing && handleFix(m.name, mt.exchange, mt.ticker)}>
                                    {fixing ? '...' : 'Use This'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.85em', color: '#999' }}>No matches found in stock_symbols.</div>
                    )}
                    <div style={{ marginTop: '6px', fontSize: '0.85em', borderTop: '1px solid #ddd', paddingTop: '6px' }}>
                      <span style={{ fontWeight: 'bold' }}>Manual entry: </span>
                      <select value={manualExchange} onChange={e => setManualExchange(e.target.value)}
                        style={{ fontSize: '0.95em', marginRight: '4px' }}>
                        <option value="TSE">TSE</option>
                        <option value="CVE">CVE</option>
                        <option value="CSE">CSE</option>
                      </select>
                      <input placeholder="Ticker" value={manualTicker}
                        onChange={e => setManualTicker(e.target.value.toUpperCase())}
                        style={{ ...fixStyles.input, width: '8em', marginRight: '4px' }} />
                      <span style={{ ...fixStyles.actionLink, color: '#090' }}
                        onClick={() => manualTicker && !fixing && handleFix(m.name, manualExchange, manualTicker)}>
                        Apply
                      </span>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const fixStyles: { [key: string]: React.CSSProperties } = {
  table: { width: '100%', marginTop: '0.5em', border: '2px solid #0d58e6', borderCollapse: 'collapse' as const },
  th: { border: '1px solid #0d58e6', padding: '4px 6px', backgroundColor: '#cce6ff', fontWeight: 'bold', fontSize: '0.8em', textAlign: 'center' as const },
  td: { border: '1px solid #0d58e6', padding: '3px 6px', fontSize: '0.8em' },
  input: { padding: '2px 4px', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  button: { padding: '4px 16px', backgroundColor: '#ccdcfc', color: '#000', border: '1px solid #808080', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '0.85em' },
  actionLink: { color: '#0069cc', textDecoration: 'none', cursor: 'pointer', fontSize: '0.9em' },
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '1em 0.5em', backgroundColor: '#fff', minHeight: 'calc(100vh - 6em)' },
  heading: { color: '#0069cc', marginTop: '1em', textAlign: 'center' },
  tabs: { display: 'flex', gap: '0', marginBottom: '1em', borderBottom: '2px solid #0d58e6' },
  tab: { padding: '6px 16px', cursor: 'pointer', fontSize: '0.85em', fontWeight: 'bold', backgroundColor: '#e6f2ff', border: '1px solid #0d58e6', borderBottom: 'none', marginRight: '2px' },
  activeTab: { backgroundColor: '#cce6ff', color: '#000' },
  searchBar: { backgroundColor: '#ececec', padding: '8px 12px', fontSize: '0.85em', marginBottom: '1em' },
  input: { padding: '2px 4px', fontFamily: 'Arial, sans-serif', fontSize: '1em', width: '10em' },
  button: { padding: '4px 16px', backgroundColor: '#ccdcfc', color: '#000', border: '1px solid #808080', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  table: { width: '100%', marginTop: '0.5em', border: '2px solid #0d58e6', borderCollapse: 'collapse' as const },
  th: { border: '1px solid #0d58e6', padding: '4px 6px', backgroundColor: '#cce6ff', fontWeight: 'bold', fontSize: '0.8em', textAlign: 'center' as const },
  td: { border: '1px solid #0d58e6', padding: '3px 6px', fontSize: '0.8em' },
  actionLink: { color: '#0069cc', textDecoration: 'none', cursor: 'pointer', fontSize: '0.9em' },
};

export default SediPage;
